import { mergeAttributes } from "@tiptap/core";
import { ResizableImage } from "mui-tiptap";
import { UPLOADED_IMAGE_FILE_ID_ATTRIBUTE } from "../../../utilities/htmlHelper";

const parseAspectRatio = (element: HTMLElement): string | null => {
  const [width, height = 1] = element.style.aspectRatio.split("/").map(parseFloat);
  const ratio = (width ?? NaN) / height;
  return Number.isFinite(ratio) && ratio > 0 ? String(ratio) : null;
};

const getLayoutStyle = (textAlign: unknown): string =>
  [
    "display: block",
    "max-width: 100%",
    ...(textAlign === "center" || textAlign === "right" ? ["margin-left: auto"] : []),
    ...(textAlign === "center" ? ["margin-right: auto"] : []),
  ].join("; ");

const HtmlEditorImageExtension = ResizableImage.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      fileId: {
        default: null,
        parseHTML: (element) => element.getAttribute(UPLOADED_IMAGE_FILE_ID_ATTRIBUTE),
        renderHTML: ({ fileId }) => (fileId ? { [UPLOADED_IMAGE_FILE_ID_ATTRIBUTE]: fileId } : {}),
      },
      aspectRatio: {
        default: null,
        parseHTML: parseAspectRatio,
        // The server-side CSS sanitizer drops a bare-number aspect-ratio but keeps the `a / b` form.
        renderHTML: ({ aspectRatio }) => (aspectRatio ? { style: `aspect-ratio: ${aspectRatio} / 1` } : {}),
      },
    };
  },

  parseHTML() {
    return [{ tag: `img[${UPLOADED_IMAGE_FILE_ID_ATTRIBUTE}]` }];
  },

  // A bare <img> ignores text-align, so the alignment is also written as inline layout for every renderer.
  renderHTML({ node, HTMLAttributes }) {
    return [
      "img",
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        height: "auto",
        style: getLayoutStyle(node.attrs["textAlign"]),
      }),
    ];
  },

  // The inherited `![alt](url)` input rule would create an image node without a fileId.
  addInputRules() {
    return [];
  },
});

export default HtmlEditorImageExtension;
